import { GroupType } from '@aws-sdk/client-cognito-identity-provider';
import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { getGlobalAdminName } from '@/utils/helpers';

type Props = {
  userGroups: string[];
  allGroups: GroupType[];
  onChange?: (newGroupList: string[]) => void;
  teamName: string;
  isGlobalAdmin: boolean;
  isGroupAdmin: boolean;
  showSelectedGroup?: boolean;
  disabled?: boolean;
};

// displays the group with a checkmark and handles the visibility of the checkmark internally
const SelectUserGroup = ({
  userGroups,
  allGroups,
  onChange,
  teamName,
  isGlobalAdmin,
  isGroupAdmin,
  showSelectedGroup = true,
  disabled = false,
}: Props) => {
  const globalAdminName = getGlobalAdminName();
  const [selectedUserGroups, setselectedUserGroups] = useState(userGroups);

  // the current selected simple group name is the first group that is not a groupadmin
  const selectedGrouName = selectedUserGroups.find((group) => !group?.toLowerCase().includes('groupadmin')) || '';

  // additional check if the current group of the selected user is a global admin group. Needed for the checkbox to block non-global admins
  const currentGroupIsGlobalAdminGroup = userGroups
    .filter((group) => group?.toLowerCase().includes(globalAdminName))
    .some((group) => group?.toLowerCase().includes(selectedGrouName));

  // based on the selected group name, check if the user is a group admin
  const selectedUserIsGroupAdmin = selectedUserGroups
    .filter((groupName) => groupName.toLowerCase().includes(selectedGrouName))
    .some((group) => group?.toLowerCase().includes('groupadmin'));

  // check if the user is a global admin by checking if the global admin group is in the selected groups
  const selectedUserIsGlobalAdmin = selectedUserGroups.some((group) => group?.toLowerCase().includes(globalAdminName));

  const handleGroupChange = (newGroup: string) => {
    setselectedUserGroups(() => [newGroup]);
  };

  const handleGroupCheckboxChange = (checked: boolean) => {
    const currSelectedTeamName = selectedUserGroups.find((group) => !group?.toLowerCase().includes('groupadmin')) || '';
    const allAvailableAdminGroups = allGroups.filter((group) => group.GroupName?.includes('groupadmin'));
    const groupAdminName =
      allAvailableAdminGroups.find((group) => group?.GroupName?.toLowerCase().includes(currSelectedTeamName))
        ?.GroupName || '';

    checked
      ? setselectedUserGroups((prev) => [...new Set([...prev, groupAdminName])])
      : setselectedUserGroups((prev) => prev.filter((group) => group !== groupAdminName));
  };

  const handleGlobalAdminCheckboxChange = (checked: boolean) => {
    checked
      ? setselectedUserGroups((prev) => [...new Set([...prev, globalAdminName])])
      : setselectedUserGroups((prev) => prev.filter((group) => group !== globalAdminName));
  };

  // every time the selected user groups change, call the onChange function with the new group list and the current group list
  useEffect(() => {
    onChange?.(selectedUserGroups);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserGroups]);

  return (
    <div
      className="w-full flex flex-col items-start gap-2"
      onClick={(e) => {
        e.preventDefault();
      }}>
      {showSelectedGroup && (
        <Select
          onValueChange={handleGroupChange}
          disabled={!isGlobalAdmin}
          defaultValue={isGlobalAdmin ? selectedGrouName : teamName}>
          <SelectTrigger className="min-w-[120px] mb-1 capitalize">
            <SelectValue placeholder="Verein auswählen" />
          </SelectTrigger>
          <SelectContent>
            {allGroups.map((group) => {
              if (group.GroupName?.toLowerCase().includes('groupadmin') || !group.GroupName) {
                return null;
              }

              return (
                <SelectItem
                  key={group.GroupName}
                  value={group.GroupName}
                  disabled={!isGlobalAdmin && !group.GroupName?.includes(teamName)}
                  className="capitalize">
                  {group.GroupName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox
          id="group-admin"
          checked={selectedUserIsGroupAdmin}
          onCheckedChange={handleGroupCheckboxChange}
          disabled={disabled || !isGroupAdmin || (currentGroupIsGlobalAdminGroup && !isGlobalAdmin)}
        />
        <label
          htmlFor="group-admin"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {selectedGrouName && <span className=" capitalize">{selectedGrouName}-</span>}
          Gruppenadministrator
        </label>
      </div>

      {isGlobalAdmin && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="global-admin"
            checked={selectedUserIsGlobalAdmin}
            onCheckedChange={handleGlobalAdminCheckboxChange}
            disabled={disabled}
          />
          <label
            htmlFor="global-admin"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Icehawk-Administrator
          </label>
        </div>
      )}
    </div>
  );
};

export default SelectUserGroup;
